import { Subscription } from '@aibees/shared/definition';
import {
  CreditsSocketMessage,
  SubscriptionsSocketMessage,
} from '@aibees/shared/features/billing-profile/socket/types';
import { useCreditUpdateSocketListener } from '@aibees/shared/features/billing-profile/socket/use-credit-update-socket-listener';
import { useSubscriptionsUpdateSocketListener } from '@aibees/shared/features/billing-profile/socket/use-subscription-update-socket-listener';
import { baseQueryClient } from '@aibees/shared/http';
import { useBaseQuery } from '@aibees/shared/http/hooks';
import { useCustomerEndpoint, useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useMemo } from 'react';
import {
  CurrentSubscriptionRequest,
  CurrentSubscriptionResponse,
} from './types';

export function useCurrentSubscriptionApi() {
  const endpoint = useCustomerEndpoint('subscription');
  const workspaceId = useWorkspace()?.id;
  const queryKey = useMemo(
    () => [useCurrentSubscriptionApi.name, workspaceId],
    [workspaceId]
  );

  const api = useBaseQuery<
    CurrentSubscriptionRequest,
    CurrentSubscriptionResponse
  >(
    queryKey,
    {
      url: endpoint,
    },
    {
      enabled: Boolean(workspaceId),
    }
  );

  const onCreditUpdateEvent = useCallback(
    (data: CreditsSocketMessage) => {
      baseQueryClient.setQueryData(
        queryKey,
        (prev: Subscription | undefined) => ({
          ...prev,
          credit: data.message,
        })
      );
    },
    [queryKey]
  );

  const onSubscriptionsUpdateEvent = useCallback(
    (data: SubscriptionsSocketMessage) => {
      baseQueryClient.setQueryData(queryKey, () => data.message);
    },
    [queryKey]
  );

  useCreditUpdateSocketListener(onCreditUpdateEvent);
  useSubscriptionsUpdateSocketListener(onSubscriptionsUpdateEvent);

  return {
    ...api,
    data:
      baseQueryClient.getQueryData<Subscription>([
        useCurrentSubscriptionApi.name,
      ]) ?? api.data,
  };
}
